import React from 'react';

import {
  Button,
  Divider,
  Group,
  Input,
  LoadingOverlay,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  TextInput,
  Textarea,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import ColorInput from 'components/ColorInput';
import Drawer from 'components/Drawer';
import ExpandableSection from 'components/ExpandableSection';
import LocationAutoComplete, {
  LocationAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/LocationAutoComplete';
import MoneyField from 'components/MoneyField';
import { useAuth } from 'context/auth-context';
import useKiloClassType from 'hooks/classType/useClassType';
import useCreateKiloClassType from 'hooks/classType/useCreateClassType';
import useUpdateKiloClassType from 'hooks/classType/useUpdateClassType';
import useGym from 'hooks/gym/useGym';
import { TimeUnitEnum } from 'types/enums';
import { setMaxLateCancellationIntervalCount } from 'utils/functions';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import { KiloClassTypeWrite } from '../../../../../types';

interface FormValues {
  name: string;
  kiloClassTypeId: string;
  description: string | null;
  duration: number;
  color: string;
  capacity: number;
  waitlistLimit: number;
  reservationAndCancellationSettings: {
    useLateCancelFee: boolean;
    useNoShowFee: boolean;
    reservationWindowOpenInterval: 'minute' | 'hour' | 'day';
    reservationWindowOpenIntervalCount: number;
    reservationWindowCloseInterval: 'minute' | 'hour' | 'day';
    reservationWindowCloseIntervalCount: number;
    allowLateCancellation: boolean;
    cancellationInterval: 'minute' | 'hour' | 'day';
    cancellationIntervalCount: number;
    cancellationFee: number;
    cancellationCreditDeduction: boolean;
    noShowFee: number;
    noShowCreditDeduction: boolean;
    waitlistMax: number;
  };
  location: LocationAutoCompleteValue;
}

interface IDrawerClassTypeFormProps {
  formId: string;
  title: JSX.Element;
  opened: boolean;
  closeHandler: () => void;
  kiloClassTypeId?: string;
  type: 'create' | 'edit';
  permission?: boolean;
}

const timeUnitOptions = [
  { value: TimeUnitEnum.MINUTE, label: `Min(s)` },
  { value: TimeUnitEnum.HOUR, label: `Hour(s)` },
  { value: TimeUnitEnum.DAY, label: `Day(s)` },
];

const DrawerClassTypeForm = ({
  formId,
  title,
  opened,
  closeHandler,
  kiloClassTypeId,
  type,
  permission = true,
}: IDrawerClassTypeFormProps): JSX.Element => {
  const { session } = useAuth();
  const { data: gymData } = useGym(session?.gym?.gymId);

  const createMutation = useCreateKiloClassType();
  const updateMutation = useUpdateKiloClassType();
  const { data, isLoading, isSuccess, isFetching } = useKiloClassType(kiloClassTypeId);

  const [isAdditionalSettingsOpened, { toggle: toggleIsAdditionalSettingsOpened }] = useDisclosure(false);

  const getReservationAndCancellationSettingsValue =
    (): FormValues['reservationAndCancellationSettings'] => {
      const kiloClassTypeData = kiloClassTypeId
        ? data?.data?.reservationAndCancellationSettings
        : gymData?.data?.eventSettings?.classes;

      return {
        useLateCancelFee: kiloClassTypeData?.cancellationFee !== 0,
        useNoShowFee: kiloClassTypeData?.noShowFee !== 0,
        reservationWindowOpenInterval: kiloClassTypeData?.reservationWindowOpenInterval ?? 'day',
        reservationWindowOpenIntervalCount: kiloClassTypeData?.reservationWindowOpenIntervalCount ?? 7,
        reservationWindowCloseInterval: kiloClassTypeData?.reservationWindowCloseInterval ?? 'hour',
        reservationWindowCloseIntervalCount: kiloClassTypeData?.reservationWindowCloseIntervalCount ?? 1,
        allowLateCancellation: kiloClassTypeData?.allowLateCancellation ?? true,
        cancellationInterval: kiloClassTypeData?.cancellationInterval ?? 'minute',
        cancellationIntervalCount: kiloClassTypeData?.cancellationIntervalCount ?? 0,
        cancellationFee: kiloClassTypeData?.cancellationFee ?? 0,
        cancellationCreditDeduction: !!kiloClassTypeData?.cancellationCreditDeduction,
        noShowFee: kiloClassTypeData?.noShowFee ?? 0,
        noShowCreditDeduction: !!kiloClassTypeData?.noShowCreditDeduction,
        waitlistMax: kiloClassTypeData?.waitlistMax ?? 5,
      };
    };

  const form = useForm<FormValues>({
    initialValues: {
      kiloClassTypeId: '',
      name: '',
      description: '',
      duration: 60,
      color: '',
      capacity: 10,
      reservationAndCancellationSettings: getReservationAndCancellationSettingsValue(),
      waitlistLimit: gymData?.data?.eventSettings?.classes?.waitlistMax || 0,
      location: {
        locationId: '',
        value: '',
        label: '',
      },
    },

    validate: {
      color: value => (value && value.length === 7 ? null : 'You must provide a valid color'),
      duration: value => (!value ? 'Duration must be provided' : null),
      capacity: value => (!value ? 'Capacity must be provided' : null),
      description: value =>
        value && value.length > 250 ? 'Ensure this field has no more than 250 characters.' : null,
    },
  });

  const handleCloseAndReset = (): void => {
    closeHandler();
    form.reset();
  };

  const handleLocationChange = (item: LocationAutoCompleteValue | null): void => {
    const selectedLocation = {
      locationId: item ? item.locationId : '',
      value: item ? item.value : '',
      label: item ? item.label : '',
    };
    form.setFieldValue('location', selectedLocation);
  };

  const handleSubmit = (values: FormValues): void => {
    const { errorNotification, successNotification } = notificationHandler();

    const payload: KiloClassTypeWrite = {
      ...values,
      reservationAndCancellationSettings: {
        reservationWindowOpenInterval:
          values.reservationAndCancellationSettings.reservationWindowOpenInterval,
        reservationWindowOpenIntervalCount:
          values.reservationAndCancellationSettings.reservationWindowOpenIntervalCount,
        reservationWindowCloseInterval:
          values.reservationAndCancellationSettings.reservationWindowCloseInterval,
        reservationWindowCloseIntervalCount:
          values.reservationAndCancellationSettings.reservationWindowCloseIntervalCount,
        allowLateCancellation: values.reservationAndCancellationSettings.allowLateCancellation,
        cancellationInterval: values.reservationAndCancellationSettings.cancellationInterval,
        cancellationIntervalCount: values.reservationAndCancellationSettings.cancellationIntervalCount,
        cancellationFee:
          values.reservationAndCancellationSettings.useLateCancelFee &&
          values.reservationAndCancellationSettings.allowLateCancellation
            ? values.reservationAndCancellationSettings.cancellationFee
            : 0,
        cancellationCreditDeduction: values.reservationAndCancellationSettings.allowLateCancellation
          ? values.reservationAndCancellationSettings.cancellationCreditDeduction
          : false,
        noShowFee: values.reservationAndCancellationSettings.useNoShowFee
          ? values.reservationAndCancellationSettings.noShowFee
          : 0,
        noShowCreditDeduction: values.reservationAndCancellationSettings.noShowCreditDeduction,
        waitlistMax: values.waitlistLimit,
      },
      location: values.location?.locationId || null,
    };

    if (type === 'edit') {
      updateMutation.mutate(payload, {
        onError: error => {
          errorNotification('Class Type update failed', error.response?.data.message);
        },
        onSuccess: response => {
          successNotification('Class Type update success', `${response.data.name} successfully updated.`);
          handleCloseAndReset();
        },
      });
    } else {
      createMutation.mutate(
        {
          ...payload,
          kiloClassTypeId: uuidv4(),
        },
        {
          onError: error => {
            errorNotification('Class Type creation failed', error.response?.data.message);
          },
          onSuccess: response => {
            successNotification(
              'Class Type creation success',
              `${response.data.name} successfully created.`,
            );
            handleCloseAndReset();
          },
        },
      );
    }
  };

  useShallowEffect(() => {
    if (type === 'edit' && data?.data && isSuccess) {
      form.setValues({
        ...data.data,
        description: data.data.description || '',
        reservationAndCancellationSettings: getReservationAndCancellationSettingsValue(),
        waitlistLimit: data.data.waitlistLimit || 0,
        location: {
          locationId: data.data.location?.locationId || '',
          value: data.data.location?.locationId || '',
          label: data.data.location?.name || '',
        },
      });
      form.resetDirty();
    }
  }, [opened, data?.data]);

  useShallowEffect(() => {
    if (type === 'create' && gymData?.data) {
      form.setValues({
        ...gymData.data,
        waitlistLimit: gymData.data.eventSettings?.classes?.waitlistMax ?? 0,
        reservationAndCancellationSettings: getReservationAndCancellationSettingsValue(),
      });
    }
  }, [gymData?.data]);

  return (
    <Drawer
      opened={opened}
      onClose={handleCloseAndReset}
      size={550}
      title={title}
      closeOnClickOutside
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            disabled={!permission}
            form={formId}
            type='submit'
            loading={createMutation.isLoading || updateMutation.isLoading || isLoading || isFetching}
            data-cy='saveClassButton'
          >
            Save
          </Button>
        </Group>
      }
    >
      <LoadingOverlay visible={type === 'edit' && (isLoading || isFetching)} />

      <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing='xs'>
          <Group grow align='top'>
            <TextInput
              disabled={!permission}
              {...form.getInputProps('name')}
              label='Name'
              placeholder='Enter class name'
              required
              data-cy='classNameInput'
            />
            <ColorInput
              disabled={!permission}
              label='Color'
              placeholder='Set color'
              required
              {...form.getInputProps('color')}
              data-cy='colorInput'
            />
          </Group>

          <Group grow align='top'>
            <LocationAutoComplete
              label='Location'
              placeholder='Select a location'
              value={form.values.location}
              onChange={handleLocationChange}
              error={form.errors.location}
              data-cy='locationInput'
              disabled={!permission}
            />
            <NumberInput
              {...form.getInputProps('capacity')}
              label='Capacity'
              placeholder='Enter class capacity'
              min={1}
              required
              disabled={!permission}
              data-cy='capacityInput'
            />
          </Group>

          <Group grow align='top'>
            <NumberInput
              {...form.getInputProps('duration')}
              label='Duration in minutes'
              placeholder='Enter duration'
              min={15}
              required
              disabled={!permission}
              data-cy='durationInput'
            />
            <div />
          </Group>

          <Textarea
            label='Description'
            placeholder='Description of this class type'
            {...form.getInputProps('description')}
            maxLength={250}
            disabled={!permission}
          />

          <Divider mt='lg' mb={0} mx='-xl' />

          <ExpandableSection
            title='Additional Settings'
            opened={isAdditionalSettingsOpened}
            handleToggle={toggleIsAdditionalSettingsOpened}
          >
            <Stack spacing='sm' px='md'>
              <Input.Wrapper label='Waitlist capacity'>
                <SimpleGrid cols={2}>
                  <NumberInput
                    disabled={!permission}
                    min={0}
                    {...form.getInputProps('waitlistLimit')}
                    required
                    data-cy='classTypeWaitlistCapacity'
                  />
                </SimpleGrid>
              </Input.Wrapper>
              <Input.Wrapper label='The amount of time before a class when reservations will be accepted'>
                <SimpleGrid cols={2}>
                  <NumberInput
                    min={0}
                    {...form.getInputProps(
                      'reservationAndCancellationSettings.reservationWindowOpenIntervalCount',
                    )}
                    disabled={!permission}
                    required
                    data-cy='classTypeReservationTimeAccepted'
                  />
                  <Select
                    data={timeUnitOptions}
                    {...form.getInputProps(
                      'reservationAndCancellationSettings.reservationWindowOpenInterval',
                    )}
                    disabled={!permission}
                  />
                </SimpleGrid>
              </Input.Wrapper>
              <Input.Wrapper label='The amount of time before a class when reservations will no longer be accepted'>
                <SimpleGrid cols={2}>
                  <NumberInput
                    min={0}
                    {...form.getInputProps(
                      'reservationAndCancellationSettings.reservationWindowCloseIntervalCount',
                    )}
                    required
                    disabled={!permission}
                    data-cy='classTypeReservationTimeNotAccepted'
                  />
                  <Select
                    data={timeUnitOptions}
                    {...form.getInputProps(
                      'reservationAndCancellationSettings.reservationWindowCloseInterval',
                    )}
                    disabled={!permission}
                  />
                </SimpleGrid>
              </Input.Wrapper>
              <Input.Wrapper>
                <SimpleGrid cols={1}>
                  <Switch
                    my='xs'
                    disabled={!permission}
                    label='Allow late cancellations'
                    checked={form.values.reservationAndCancellationSettings.allowLateCancellation}
                    {...form.getInputProps('reservationAndCancellationSettings.allowLateCancellation')}
                  />
                </SimpleGrid>
              </Input.Wrapper>
              <Input.Wrapper
                label={
                  form.values.reservationAndCancellationSettings.allowLateCancellation
                    ? 'Time before the start of a class when a cancellation is considered late'
                    : 'Time before the start of a class when cancellations are no longer allowed'
                }
              >
                <SimpleGrid cols={2}>
                  <NumberInput
                    disabled={!permission}
                    min={0}
                    max={setMaxLateCancellationIntervalCount(
                      form.values.reservationAndCancellationSettings.cancellationInterval,
                    )}
                    {...form.getInputProps('reservationAndCancellationSettings.cancellationIntervalCount')}
                    required
                    data-cy='classTypeLateCancel'
                  />
                  <Select
                    disabled={!permission}
                    data={timeUnitOptions}
                    {...form.getInputProps('reservationAndCancellationSettings.cancellationInterval')}
                  />
                </SimpleGrid>
              </Input.Wrapper>
              {form.values.reservationAndCancellationSettings.allowLateCancellation && (
                <>
                  <Group noWrap>
                    <Switch
                      disabled={!permission}
                      checked={form.values.reservationAndCancellationSettings.useLateCancelFee}
                      {...form.getInputProps('reservationAndCancellationSettings.useLateCancelFee')}
                    />
                    <Text size='sm'>Auto-Charge Fee for Late Cancellations</Text>
                    {form.values.reservationAndCancellationSettings.useLateCancelFee && (
                      <MoneyField
                        country={session?.gym?.country}
                        disabled={!permission}
                        min={0}
                        {...form.getInputProps('reservationAndCancellationSettings.cancellationFee')}
                        data-cy='classTypeLateCancelAutoCharge'
                      />
                    )}
                  </Group>
                  <Input.Wrapper>
                    <SimpleGrid cols={1}>
                      <Switch
                        my='xs'
                        disabled={!permission}
                        label='Deduction of credit for late cancellation'
                        checked={form.values.reservationAndCancellationSettings.cancellationCreditDeduction}
                        {...form.getInputProps(
                          'reservationAndCancellationSettings.cancellationCreditDeduction',
                        )}
                        data-cy='classTypeLateCancelDeductionCredit'
                      />
                    </SimpleGrid>
                  </Input.Wrapper>
                </>
              )}
              <Divider />

              <Group noWrap>
                <Switch
                  disabled={!permission}
                  checked={form.values.reservationAndCancellationSettings.useNoShowFee}
                  {...form.getInputProps('reservationAndCancellationSettings.useNoShowFee')}
                />
                <Text size='sm'>Auto-Charge Fee for No Shows</Text>
                {form.values.reservationAndCancellationSettings.useNoShowFee && (
                  <MoneyField
                    country={session?.gym?.country}
                    disabled={!permission}
                    min={0}
                    {...form.getInputProps('reservationAndCancellationSettings.noShowFee')}
                    data-cy='classTypeAutoChargeNoShow'
                  />
                )}
              </Group>
              <Input.Wrapper>
                <SimpleGrid cols={1}>
                  <Switch
                    my='xs'
                    disabled={!permission}
                    label='Deduction of credit for No Shows'
                    checked={form.values.reservationAndCancellationSettings.noShowCreditDeduction}
                    {...form.getInputProps('reservationAndCancellationSettings.noShowCreditDeduction')}
                    data-cy='classTypeDeductionCreditNoShow'
                  />
                </SimpleGrid>
              </Input.Wrapper>
            </Stack>
          </ExpandableSection>
        </Stack>
      </form>
    </Drawer>
  );
};

export default DrawerClassTypeForm;
